import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, RefreshCw, Edit3, Eye, EyeOff, AlertTriangle,
  CheckCircle2, ExternalLink, Calendar, Clock, X, Trash2, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

const EMPTY_FORM = {
  namn: "",
  url: "",
  typ: "ics",
  trust: "review_required",
  region: "",
  kategori: "",
  active: true,
};

const SOURCE_TYPES = [
  { value: "ics", label: "ICS-kalender (iCalendar-feed)" },
  { value: "html_stromsund_events", label: "Strömsund evenemangssida (HTML)" },
  { value: "source_watch", label: "Källbevakning (endast övervakning)" },
  { value: "housing_rent_watch", label: "Hyresbostäder (Strömsunds Hyresbostäder)" },
];

const TRUST_LEVELS = [
  { value: "review_required",   label: "review_required (granskning först)" },
  { value: "auto_publish",      label: "auto_publish (publicera direkt)" },
  { value: "source_watch_only", label: "source_watch_only (endast bevaka)" },
  { value: "review_sensitive",  label: "review_sensitive (känslig — HEAD/etag)" },
  { value: "official_review",   label: "official_review (officiell — granskning)" },
  { value: "market_review",     label: "market_review (marknad — bot-blockering tolereras)" },
  { value: "disabled",          label: "disabled (kontrolleras aldrig)" },
];

function typeLabel(typ) {
  const found = SOURCE_TYPES.find((t) => t.value === typ);
  return found ? found.label : (typ || "ics");
}

function trustLabel(trust) {
  const found = TRUST_LEVELS.find((t) => t.value === trust);
  return found ? found.label.split(" (")[0] : (trust || "review_required");
}

function isMaster(user) {
  return user && (user.role === "master" || user.role === "admin");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sv-SE", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ModEventSources() {
  const token = sessionStorage.getItem("mod_token");
  const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");

  const [sources, setSources] = useState([]);
  const [reports, setReports] = useState([]);
  const [latest, setLatest] = useState(null);
  const [watchLatest, setWatchLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [watching, setWatching] = useState(false);
  const [propertyWatching, setPropertyWatching] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, l, wl] = await Promise.all([
        axios.get(`${API}/mod/event-sources`, { headers: modHeaders() }),
        axios.get(`${API}/mod/event-sync/reports?limit=10`, { headers: modHeaders() }),
        axios.get(`${API}/mod/event-sync/reports/latest`, { headers: modHeaders() }),
        axios.get(`${API}/mod/source-watch/reports/latest`, { headers: modHeaders() }),
      ]);
      setSources(s.data || []);
      setReports(r.data || []);
      setLatest(l.data && l.data.started_at ? l.data : null);
      setWatchLatest(wl.data && wl.data.started_at ? wl.data : null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kunde inte hämta källor";
      toast.error(typeof msg === "string" ? msg : "Fel vid hämtning");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (token) fetchAll();
  }, [token, fetchAll]);

  if (!token) {
    // Bewahre den vollständigen Ziel-URL, damit Bookmarklet-Parameter nach Login erhalten bleiben.
    const returnTo = window.location.pathname + window.location.search;
    return <Navigate to={`/mod/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  const canEdit = isMaster(user);

  const startCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const startEdit = (src) => {
    setEditId(src.id);
    setForm({
      namn: src.namn || "",
      url: src.url || "",
      typ: src.typ || "ics",
      trust: src.trust || "review_required",
      region: src.region || "",
      kategori: src.kategori || "",
      active: !!src.active,
    });
    setFormError(null);
    setShowForm(true);
  };

  const saveForm = async () => {
    setFormError(null);
    if (!form.namn.trim()) { setFormError("Namn saknas."); return; }
    if (!form.url.trim()) { setFormError("URL saknas."); return; }
    try {
      const payload = {
        namn: form.namn.trim(),
        url: form.url.trim(),
        typ: form.typ || "ics",
        trust: form.trust,
        region: form.region.trim() || null,
        kategori: form.kategori.trim() || null,
        active: !!form.active,
      };
      if (editId) {
        await axios.patch(`${API}/mod/event-sources/${editId}`, payload, { headers: modHeaders() });
        toast.success("Källan uppdaterad");
      } else {
        await axios.post(`${API}/mod/event-sources`, payload, { headers: modHeaders() });
        toast.success("Källan tillagd");
      }
      setShowForm(false);
      setEditId(null);
      setForm(EMPTY_FORM);
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kunde inte spara";
      setFormError(typeof msg === "string" ? msg : "Sparning misslyckades");
    }
  };

  const toggleActive = async (src) => {
    try {
      await axios.patch(
        `${API}/mod/event-sources/${src.id}`,
        { active: !src.active },
        { headers: modHeaders() }
      );
      toast.success(src.active ? "Källan inaktiverad" : "Källan aktiverad");
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kunde inte ändra status";
      toast.error(typeof msg === "string" ? msg : "Fel");
    }
  };

  const hardDelete = async (src) => {
    if (!window.confirm(`Radera källan "${src.namn}" permanent?\n\nTips: du kan istället inaktivera den, då sparas historiken.`)) return;
    try {
      await axios.delete(`${API}/mod/event-sources/${src.id}`, { headers: modHeaders() });
      toast.success("Källan raderad");
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kunde inte radera";
      toast.error(typeof msg === "string" ? msg : "Fel");
    }
  };

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await axios.post(
        `${API}/mod/event-sync/run`, {}, { headers: modHeaders(), timeout: 120000 }
      );
      setLatest(data);
      const summary = `+${data.added_count || 0} nya · ~${data.updated_count || 0} uppdaterade · ${data.review_count || 0} review · ${data.sources_failed || 0} fel`;
      toast.success("Synk klar", { description: summary, duration: 5000 });
      // Uppdatera även historik och status
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Synk misslyckades";
      toast.error(typeof msg === "string" ? msg : "Synk misslyckades");
    }
    setSyncing(false);
  };

  const runWatch = async () => {
    if (watching) return;
    setWatching(true);
    try {
      const { data } = await axios.post(
        `${API}/mod/source-watch/run`, {}, { headers: modHeaders(), timeout: 120000 }
      );
      setWatchLatest(data);
      const summary = `${data.ok_count || 0} ok · ${data.alarm_count || 0} alarm · ${data.sensitive_alert_count || 0} känsligt · ${data.structure_changed_count || 0} struktur`;
      toast.success("Källbevakning klar", { description: summary, duration: 5000 });
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Källbevakning misslyckades";
      toast.error(typeof msg === "string" ? msg : "Källbevakning misslyckades");
    }
    setWatching(false);
  };

  const seedStartSources = async () => {
    if (seeding) return;
    if (!window.confirm("Lägg till Build 69.0-startkällor (ByaKoll, Backe.nu, Trygghet/Grannsamverkan)?\nIdempotent — befintliga hoppas över.")) return;
    setSeeding(true);
    try {
      const { data } = await axios.post(
        `${API}/mod/source-watch/seed`, {}, { headers: modHeaders(), timeout: 30000 }
      );
      toast.success("Startkällor inlästa", {
        description: `${data.inserted_count} nya · ${data.skipped_count} fanns redan`,
        duration: 5000,
      });
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Kunde inte läsa in startkällor";
      toast.error(typeof msg === "string" ? msg : "Fel vid seed");
    }
    setSeeding(false);
  };

  const seedPropertySources = async () => {
    if (seeding) return;
    if (!window.confirm("Lägg till Build 71.0-bostadskällor (Strömsunds Hyresbostäder, Hemnet/Booli som source_watch, kommunala tomter)?\nIdempotent — befintliga hoppas över.")) return;
    setSeeding(true);
    try {
      const { data } = await axios.post(
        `${API}/mod/property-watch/seed`, {}, { headers: modHeaders(), timeout: 30000 }
      );
      toast.success("Bostadskällor inlästa", {
        description: `${data.inserted_count} nya · ${data.skipped_count} fanns redan`,
        duration: 5000,
      });
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Kunde inte läsa in bostadskällor";
      toast.error(typeof msg === "string" ? msg : "Fel vid bostads-seed");
    }
    setSeeding(false);
  };

  const runPropertyWatch = async () => {
    if (propertyWatching) return;
    setPropertyWatching(true);
    try {
      const { data } = await axios.post(
        `${API}/mod/property-watch/run`, {}, { headers: modHeaders(), timeout: 120000 }
      );
      const summary = `${data.new_count || 0} nya · ${data.removed_count || 0} försvunna · ${data.items_seen_again || 0} sedda igen`;
      toast.success("Bostadsbevakning klar", { description: summary, duration: 5000 });
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Bostadsbevakning misslyckades";
      toast.error(typeof msg === "string" ? msg : "Bostadsbevakning misslyckades");
    }
    setPropertyWatching(false);
  };

  return (
    <div data-testid="mod-event-sources" className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <Link
        to="/mod"
        className="inline-flex items-center gap-1 text-bark hover:text-pine text-sm no-underline mb-4"
        data-testid="back-to-mod"
      >
        <ArrowLeft size={14} /> Tillbaka till moderationen
      </Link>

      <header className="mb-6">
        <h1 className="font-fraktur-smooth text-2xl sm:text-3xl text-falu tracking-wide mb-1">
          Evenemang-källor
        </h1>
        <p className="text-sm text-bark leading-relaxed max-w-2xl">
          Förvalta betrodda kalender-källor (ICS) och godkända offentliga evenemangssidor som hämtas automatiskt.
          Källor med <em>review_required</em> hamnar i Inskickat och måste granskas.
          Källor med <em>auto_publish</em> publiceras direkt.
        </p>
      </header>

      {/* Scheduler-info */}
      <div className="mb-6 bg-bone-alt border border-warm-border rounded-sm p-4 flex items-start gap-3">
        <Info size={18} className="text-pine shrink-0 mt-0.5" />
        <div className="text-sm text-bark leading-relaxed">
          <p>
            <strong>Schemalagd synk:</strong> en bakgrundsloop kör synk
            automatiskt varje måndag kl. 06:00 UTC (samma cykel som veckorapporten).
            Du kan när som helst trigga manuellt nedan.
          </p>
          {latest && (
            <p className="mt-1.5 text-xs text-bark/80">
              Senaste körning: <span className="font-medium">{fmtDate(latest.started_at)}</span>
              {" · "}
              +{latest.added_count || 0} nya, ~{latest.updated_count || 0} uppdaterade,
              {" "}{latest.review_count || 0} review, {latest.sources_failed || 0} fel.
            </p>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-heading text-lg font-medium text-ink">
          Källor {sources.length > 0 && <span className="text-sm text-muted-foreground">({sources.length})</span>}
        </h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={runSync}
            disabled={syncing || !canEdit}
            data-testid="sync-now-btn"
            className="inline-flex items-center gap-2 bg-falu hover:bg-falu-hover disabled:opacity-50 disabled:cursor-not-allowed text-parchment font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors"
            title={!canEdit ? "Endast master kan starta synk" : "Hämta alla aktiva event-källor nu"}
          >
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Synkar…" : "Synka events nu"}
          </button>
          <button
            onClick={runWatch}
            disabled={watching || !canEdit}
            data-testid="watch-now-btn"
            className="inline-flex items-center gap-2 bg-bark hover:bg-bark/85 disabled:opacity-50 disabled:cursor-not-allowed text-parchment font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors"
            title={!canEdit ? "Endast master kan starta källbevakning" : "Kontrollera alla aktiva källors tillgänglighet"}
          >
            <Eye size={14} className={watching ? "animate-pulse" : ""} />
            {watching ? "Bevakar…" : "Kör källbevakning nu"}
          </button>
          <button
            onClick={runPropertyWatch}
            disabled={propertyWatching || !canEdit}
            data-testid="property-watch-now-btn"
            className="inline-flex items-center gap-2 bg-bark hover:bg-bark/85 disabled:opacity-50 disabled:cursor-not-allowed text-parchment font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors"
            title={!canEdit ? "Endast master kan starta bostadsbevakning" : "Hämta hyresbostäder från Strömsunds Hyresbostäder"}
          >
            <Eye size={14} className={propertyWatching ? "animate-pulse" : ""} />
            {propertyWatching ? "Bevakar…" : "Kör bostadsbevakning nu"}
          </button>
          {canEdit && (
            <>
              <button
                onClick={seedStartSources}
                disabled={seeding}
                data-testid="seed-sources-btn"
                className="inline-flex items-center gap-2 bg-pine hover:bg-pine-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors"
                title="Lägg till Build 69.0-startkällor (idempotent)"
              >
                <Plus size={14} /> Lägg till startkällor
              </button>
              <button
                onClick={seedPropertySources}
                disabled={seeding}
                data-testid="seed-property-btn"
                className="inline-flex items-center gap-2 bg-pine hover:bg-pine-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors"
                title="Lägg till Build 71.0-bostadskällor (idempotent)"
              >
                <Plus size={14} /> Lägg till bostadskällor
              </button>
              <button
                onClick={startCreate}
                data-testid="add-source-btn"
                className="inline-flex items-center gap-2 border border-warm-border bg-white hover:bg-bone-alt text-bark font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors"
              >
                <Plus size={14} /> Egen källa
              </button>
            </>
          )}
        </div>
      </div>

      {/* Källista */}
      {loading ? (
        <p className="text-muted-foreground py-6" data-testid="sources-loading">Laddar…</p>
      ) : sources.length === 0 ? (
        <div data-testid="sources-empty" className="bg-bone-alt border border-warm-border rounded-sm p-8 text-center">
          <Calendar size={28} className="mx-auto text-pine/60 mb-2" />
          <p className="text-bark">Inga källor tillagda ännu.</p>
          {canEdit && (
            <p className="text-sm text-muted-foreground mt-1">
              Klicka på &ldquo;Lägg till källa&rdquo; för att registrera din första ICS-feed.
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="sources-list">
          {sources.map((src) => (
            <SourceCard
              key={src.id}
              src={src}
              canEdit={canEdit}
              onEdit={() => startEdit(src)}
              onToggle={() => toggleActive(src)}
              onDelete={() => hardDelete(src)}
            />
          ))}
        </div>
      )}

      {/* Form (skapa / redigera) */}
      {showForm && (
        <SourceForm
          editId={editId}
          form={form}
          setForm={setForm}
          formError={formError}
          onSave={saveForm}
          onCancel={() => { setShowForm(false); setEditId(null); setFormError(null); }}
        />
      )}

      {/* Senaste källbevakning */}
      <section className="mt-10" data-testid="latest-watch">
        <h2 className="font-heading text-lg font-medium text-ink mb-3">Källbevakning – senaste rapport</h2>
        {watchLatest ? (
          <WatchReportCard report={watchLatest} />
        ) : (
          <div className="bg-bone-alt border border-warm-border rounded-sm p-4 text-sm text-muted-foreground">
            Källbevakning har inte körts ännu. Klicka "Kör källbevakning nu" för att starta första kontrollen.
            <br />Automatisk körning sker varannan vecka (jämna ISO-veckor, måndag 06:00 UTC).
          </div>
        )}
      </section>

      {/* Senaste synk-rapport */}
      <section className="mt-10" data-testid="latest-report">
        <h2 className="font-heading text-lg font-medium text-ink mb-3">Senaste synk-rapport</h2>
        {latest ? (
          <ReportCard report={latest} expanded={true} />
        ) : (
          <div className="bg-bone-alt border border-warm-border rounded-sm p-4 text-sm text-muted-foreground">
            Ingen synk-runda har körts ännu.
          </div>
        )}
      </section>

      {/* Historik */}
      {reports.length > 1 && (
        <section className="mt-8" data-testid="reports-history">
          <h2 className="font-heading text-lg font-medium text-ink mb-3">Historik (senaste 10)</h2>
          <div className="flex flex-col gap-2">
            {reports.slice(1).map((rep) => (
              <ReportCard key={rep.id || rep.started_at} report={rep} expanded={false} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SourceCard({ src, canEdit, onEdit, onToggle, onDelete }) {
  return (
    <div
      data-testid={`source-${src.id}`}
      className={`bg-white border rounded-sm p-4 ${src.active ? "border-warm-border" : "border-warm-border bg-bone-alt/40 opacity-80"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-heading text-base font-medium text-ink">{src.namn}</h3>
            <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
              src.trust === "auto_publish" ? "bg-pine/15 text-pine"
              : src.trust === "review_sensitive" ? "bg-falu text-parchment"
              : src.trust === "source_watch_only" ? "bg-bark/10 text-bark"
              : src.trust === "disabled" ? "bg-muted text-muted-foreground"
              : "bg-bark/15 text-bark"
            }`}>
              {src.trust || "review_required"}
            </span>
            <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
              src.active ? "bg-falu/15 text-falu" : "bg-muted text-muted-foreground"
            }`}>
              {src.active ? <><CheckCircle2 size={10} /> aktiv</> : <><EyeOff size={10} /> inaktiv</>}
            </span>
            <span className="inline-block text-[10px] uppercase tracking-wider text-muted-foreground">
              typ: {typeLabel(src.typ)}
            </span>
            {src.last_status === "error" && (
              <span data-testid={`watch-status-error-${src.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-falu text-parchment">
                <AlertTriangle size={10} /> bevakning: fel
              </span>
            )}
            {src.last_status === "ok" && src.last_structure_changed && (
              <span data-testid={`watch-status-changed-${src.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-bark text-parchment">
                struktur ändrad
              </span>
            )}
          </div>
          <a
            href={src.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-pine hover:underline break-all"
          >
            {src.url} <ExternalLink size={10} />
          </a>
          <div className="mt-1 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
            {src.region && <span>Region: {src.region}</span>}
            {src.kategori && <span>Kategori: {src.kategori}</span>}
            <span>Skapad: {fmtDate(src.created_at)}</span>
            {src.last_checked_at && (
              <span data-testid={`last-checked-${src.id}`}>
                Senast kontrollerad: {fmtDate(src.last_checked_at)}
                {src.last_status === "error" && src.last_error && (
                  <span className="text-falu"> · {src.last_error}</span>
                )}
              </span>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={onToggle}
              data-testid={`toggle-active-${src.id}`}
              className="inline-flex items-center gap-1 text-xs border border-warm-border bg-white hover:bg-bone-alt text-bark px-2.5 py-1.5 rounded-sm transition-colors"
              title={src.active ? "Inaktivera" : "Aktivera"}
            >
              {src.active ? <><EyeOff size={12} /> Inaktivera</> : <><Eye size={12} /> Aktivera</>}
            </button>
            <button
              onClick={onEdit}
              data-testid={`edit-source-${src.id}`}
              className="inline-flex items-center gap-1 text-xs border border-warm-border bg-white hover:bg-bone-alt text-bark px-2.5 py-1.5 rounded-sm transition-colors"
            >
              <Edit3 size={12} /> Redigera
            </button>
            <button
              onClick={onDelete}
              data-testid={`delete-source-${src.id}`}
              className="inline-flex items-center gap-1 text-xs border border-falu/40 bg-white hover:bg-falu/5 text-falu px-2.5 py-1.5 rounded-sm transition-colors"
              title="Radera permanent (helst använd Inaktivera)"
            >
              <Trash2 size={12} /> Radera
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SourceForm({ editId, form, setForm, formError, onSave, onCancel }) {
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div
      data-testid="source-form-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onCancel}
    >
      <div
        className="bg-parchment border border-warm-border rounded-sm shadow-xl max-w-lg w-full my-12"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-warm-border px-5 py-3">
          <h3 className="font-heading text-base font-medium text-ink">
            {editId ? "Redigera källa" : "Lägg till ny källa"}
          </h3>
          <button onClick={onCancel} className="text-muted-foreground hover:text-ink" data-testid="form-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 flex flex-col gap-4">
          {formError && (
            <div data-testid="form-error" className="flex items-start gap-2 bg-falu/10 border border-falu/30 text-falu text-sm rounded-sm p-3">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{formError}</span>
            </div>
          )}

          <Field label="Namn *" hint="t.ex. 'Hammerdals bygdegårdsförening'">
            <input
              type="text"
              value={form.namn}
              onChange={(e) => onChange("namn", e.target.value)}
              data-testid="form-namn"
              className="w-full border border-warm-border bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
              placeholder="Källans namn"
            />
          </Field>

          <Field label="URL *" hint="ICS-feeds och godkända offentliga evenemangssidor stöds. Privata Facebook-länkar avvisas.">
            <input
              type="url"
              value={form.url}
              onChange={(e) => onChange("url", e.target.value)}
              data-testid="form-url"
              className="w-full border border-warm-border bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
              placeholder="https://..."
            />
          </Field>

          <Field label="Typ" hint="Välj källans format. Endast vitlistade typer fungerar.">
            <select
              value={form.typ}
              onChange={(e) => onChange("typ", e.target.value)}
              data-testid="form-typ"
              className="w-full border border-warm-border bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
            >
              {SOURCE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Förtroende-nivå (trust)" hint="Bestäm hur källan får uppträda: auto_publish, review_required, source_watch_only, review_sensitive eller disabled.">
            <select
              value={form.trust}
              onChange={(e) => onChange("trust", e.target.value)}
              data-testid="form-trust"
              className="w-full border border-warm-border bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
            >
              {TRUST_LEVELS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Region (valfri)">
              <input
                type="text"
                value={form.region}
                onChange={(e) => onChange("region", e.target.value)}
                data-testid="form-region"
                className="w-full border border-warm-border bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
                placeholder="t.ex. Strömsund"
              />
            </Field>
            <Field label="Kategori (valfri)">
              <input
                type="text"
                value={form.kategori}
                onChange={(e) => onChange("kategori", e.target.value)}
                data-testid="form-kategori"
                className="w-full border border-warm-border bg-white px-3 py-2 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-pine/30"
                placeholder="t.ex. Musik"
              />
            </Field>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-bark">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => onChange("active", e.target.checked)}
              data-testid="form-active"
              className="rounded border-warm-border"
            />
            Aktiv (källan ingår i synk-rundor)
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-warm-border px-5 py-3 bg-bone-alt/50">
          <button
            onClick={onCancel}
            data-testid="form-cancel"
            className="text-sm text-bark hover:text-ink px-3 py-2"
          >
            Avbryt
          </button>
          <button
            onClick={onSave}
            data-testid="form-save"
            className="inline-flex items-center gap-2 bg-pine hover:bg-pine-hover text-white font-body font-semibold text-sm px-4 py-2 rounded-sm transition-colors"
          >
            {editId ? "Spara ändringar" : "Lägg till"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function ReportCard({ report, expanded }) {
  const [open, setOpen] = useState(!!expanded);
  const r = report || {};
  const added = r.added || [];
  const updated = r.updated || [];
  const review = r.review_queue || [];
  const errors = r.errors || [];
  const archived = r.archived_titles || [];
  const sourcesSummary = r.sources_summary || [];
  const possibleDuplicates = r.possible_duplicates || [];

  return (
    <div data-testid={`report-${r.id || r.started_at}`} className="bg-white border border-warm-border rounded-sm">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-left hover:bg-bone-alt/40 transition-colors"
      >
        <div className="flex items-center gap-2 text-sm">
          <Clock size={14} className="text-muted-foreground" />
          <span className="font-medium text-ink">{fmtDate(r.started_at)}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-xs text-bark">
            {r.sources_checked || 0} källor
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <Badge color="pine">+{r.added_count || 0} nya</Badge>
          <Badge color="bark">~{r.updated_count || 0} uppd.</Badge>
          {(r.archived_count || 0) > 0 && <Badge color="muted">{r.archived_count} arkiv.</Badge>}
          {(r.review_count || 0) > 0 && <Badge color="bark">{r.review_count} review</Badge>}
          {(r.possible_duplicate_count || 0) > 0 && <Badge color="falu">{r.possible_duplicate_count} möjl. dubblett</Badge>}
          {(r.sources_failed || 0) > 0 && <Badge color="falu">{r.sources_failed} fel</Badge>}
        </div>
      </button>

      {open && (
        <div className="border-t border-warm-border px-4 py-3 text-sm flex flex-col gap-3">
          {sourcesSummary.length > 0 && (
            <ReportSection title={`Källor kontrollerade (${sourcesSummary.length})`} testid="report-sources">
              {sourcesSummary.map((s, i) => (
                <li key={i}>
                  <span className={s.status === "ok" ? "text-bark" : "text-falu"}>
                    {s.status === "ok" ? "✓" : "✗"}
                  </span>{" "}
                  {s.namn || "(utan namn)"}{" "}
                  <span className="text-muted-foreground">— {typeLabel(s.typ)} — {s.events_found ?? 0} träffar</span>
                </li>
              ))}
            </ReportSection>
          )}
          {errors.length > 0 && (
            <ReportSection title="Fel" testid="report-errors" color="falu">
              {errors.map((e, i) => (
                <li key={i} className="text-falu text-xs">
                  <strong>{e.source || "okänd källa"}{e.typ ? ` (${typeLabel(e.typ)})` : ""}:</strong> {e.msg}
                </li>
              ))}
            </ReportSection>
          )}
          {possibleDuplicates.length > 0 && (
            <ReportSection title={`Möjliga dubbletter (${possibleDuplicates.length})`} testid="report-duplicates" color="falu">
              {possibleDuplicates.slice(0, 30).map((d, i) => (
                <li key={i} className="text-falu text-xs">
                  <strong>{d.titel}</strong> — {d.datum}{" "}
                  <span className="text-muted-foreground">
                    från {d.source} · matchar befintligt &ldquo;{d.existing_titel}&rdquo; ({d.existing_source})
                  </span>
                </li>
              ))}
            </ReportSection>
          )}
          {added.length > 0 && (
            <ReportSection title={`Nya evenemang (${added.length})`} testid="report-added">
              {added.slice(0, 20).map((it, i) => (
                <li key={i}>{it.titel} — {it.datum} <span className="text-muted-foreground">({it.source})</span></li>
              ))}
            </ReportSection>
          )}
          {updated.length > 0 && (
            <ReportSection title={`Uppdaterade (${updated.length})`} testid="report-updated">
              {updated.slice(0, 20).map((it, i) => (
                <li key={i}>
                  {it.titel} — {it.datum}{" "}
                  <span className="text-muted-foreground">({(it.changed || []).join(", ")})</span>
                </li>
              ))}
            </ReportSection>
          )}
          {review.length > 0 && (
            <ReportSection title={`Behöver granskning (${review.length})`} testid="report-review">
              {review.slice(0, 20).map((it, i) => (
                <li key={i}>
                  {it.titel} — {it.datum}{" "}
                  <span className="text-muted-foreground">({it.reason})</span>
                </li>
              ))}
            </ReportSection>
          )}
          {archived.length > 0 && (
            <ReportSection title={`Arkiverade (${archived.length})`} testid="report-archived">
              {archived.slice(0, 10).map((it, i) => (
                <li key={i}>{it.titel} — {it.datum}</li>
              ))}
            </ReportSection>
          )}
          {added.length === 0 && updated.length === 0 && review.length === 0 && errors.length === 0 && archived.length === 0 && possibleDuplicates.length === 0 && (
            <p className="text-muted-foreground text-xs">Inga förändringar i denna körning.</p>
          )}
        </div>
      )}
    </div>
  );
}

function WatchReportCard({ report }) {
  const r = report || {};
  const items = r.items || [];
  const alarms = items.filter((x) => x.level === "alarm");
  const sensitives = items.filter((x) => x.level === "sensitive_alert");
  const structChanges = items.filter((x) => x.level === "review");
  const oks = items.filter((x) => x.level === "ok");
  return (
    <div data-testid="watch-report-card" className="bg-white border border-warm-border rounded-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-warm-border">
        <div className="flex items-center gap-2 text-sm">
          <Clock size={14} className="text-muted-foreground" />
          <span className="font-medium text-ink">{fmtDate(r.started_at)}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-xs text-bark">{r.sources_checked || 0} källor</span>
          {r.forced && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">(manuell)</span>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <Badge color="pine">{r.ok_count || 0} ok</Badge>
          {(r.structure_changed_count || 0) > 0 && <Badge color="bark">{r.structure_changed_count} struktur</Badge>}
          {(r.sensitive_alert_count || 0) > 0 && <Badge color="falu">{r.sensitive_alert_count} känsligt</Badge>}
          {(r.alarm_count || 0) > 0 && <Badge color="falu">{r.alarm_count} alarm</Badge>}
        </div>
      </div>
      <div className="px-4 py-3 flex flex-col gap-3 text-sm">
        {alarms.length > 0 && (
          <ReportSection title={`Alarm: källor med fel (${alarms.length})`} testid="watch-alarms" color="falu">
            {alarms.map((it) => (
              <li key={it.source_id} className="text-falu text-xs">
                <strong>{it.namn}</strong> — HTTP {it.status_code ?? "?"} · {it.message}
              </li>
            ))}
          </ReportSection>
        )}
        {sensitives.length > 0 && (
          <ReportSection title={`Känsliga ändringsindikatorer (${sensitives.length})`} testid="watch-sensitives" color="falu">
            {sensitives.map((it) => (
              <li key={it.source_id} className="text-falu text-xs">
                <strong>{it.namn}</strong> — {it.message}{" "}
                <a href={it.url} target="_blank" rel="noreferrer" className="underline">öppna officiell källa</a>
              </li>
            ))}
          </ReportSection>
        )}
        {structChanges.length > 0 && (
          <ReportSection title={`Struktur-/innehållsändringar (${structChanges.length})`} testid="watch-changes">
            {structChanges.map((it) => (
              <li key={it.source_id}>
                <strong>{it.namn}</strong> — {it.message}
              </li>
            ))}
          </ReportSection>
        )}
        {oks.length > 0 && (
          <ReportSection title={`OK (${oks.length})`} testid="watch-oks">
            {oks.map((it) => (
              <li key={it.source_id} className="text-bark">
                {it.namn} <span className="text-muted-foreground">— {it.url}</span>
              </li>
            ))}
          </ReportSection>
        )}
        {items.length === 0 && (
          <p className="text-muted-foreground text-xs">Inga källor kontrollerades i denna körning.</p>
        )}
      </div>
    </div>
  );
}



function ReportSection({ title, testid, children, color }) {
  const cls = color === "falu" ? "text-falu" : "text-ink";
  return (
    <div data-testid={testid}>
      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${cls}`}>{title}</p>
      <ul className="text-xs text-bark list-disc pl-5 space-y-0.5">{children}</ul>
    </div>
  );
}

function Badge({ color, children }) {
  const palette = {
    pine: "bg-pine/15 text-pine",
    bark: "bg-bark/15 text-bark",
    falu: "bg-falu/15 text-falu",
    muted: "bg-muted text-muted-foreground",
  };
  return (
    <span className={`inline-block font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${palette[color] || palette.bark}`}>
      {children}
    </span>
  );
}
